import axios from "axios";
import * as Sentry from "@sentry/react";
import { useAppSelector } from "../store/hook";
import {
  FAQ,
  GroupedFAQ,
  classifyFaqFailure,
  faqSources,
  groupFaqs,
  loadFaqs,
  parseJsonFaqs,
} from "./faqData";

interface FAQProject {
  title: string;
  details: string;
}

interface UseFaqHook {
  faqsData: GroupedFAQ[];
  fetchFAQData: () => Promise<GroupedFAQ[]>;
  groupFaqProjectVisible: (groupId: any) => FAQProject[];
}

export default function useFaq(): UseFaqHook {
  const { isBatteryOnly } = useAppSelector(
    (state) => state.portal || ([] as any)
  );
  const faqsData = useAppSelector((state) => state.faq || ([] as any));

  const fetchFAQData = async () => {
    const parsedData = await loadFaqs(
      faqSources(isBatteryOnly),
      async (source) => {
        const response = await axios.get(source.url);
        // JSON answers are already HTML, so they skip the CSV cell formatting.
        return source.format === "json"
          ? parseJsonFaqs(response.data)
          : parseCSV(response.data);
      },
      (source, error) => {
        console.warn(`FAQ ${source.format} source failed: ${source.url}`, error);
        if (source.format !== "json") return;

        const failure = classifyFaqFailure(error);

        // A 403 or 404 just means nothing is approved in Notion yet. That is
        // the normal state of battery.json today, so it stays silent.
        if (failure.status === 403 || failure.status === 404) return;

        // Each kind gets its own message so Sentry groups them separately.
        Sentry.captureMessage(
          `Portal FAQ JSON ${failure.summary}, using Google Sheet: ${source.url}`,
          { level: "warning", extra: { url: source.url, ...failure } }
        );
      }
    );
    return groupFaqs(parsedData);
  };

  const parseCSV = (csvText: string): FAQ[] => {
    const rows = csvText.split(/\r\n/);
    const headers = rows[0].split(/,(?![ ])/);
    const data = [];

    for (let i = 1; i < rows.length; i++) {
      const rowData = rows[i].split(/,(?![ ])/);
      const rowObject: any = {};

      let hasEmptyValue = false;

      for (let j = 0; j < headers.length; j++) {
        const key = headers[j].trim();
        const value = formatValue(rowData[j]);

        if (!value) {
          hasEmptyValue = true;
          break;
        }

        rowObject[key] = value;
      }

      if (!hasEmptyValue) {
        data.push({ ...rowObject, link: false });
      }
    }

    return data;
  };

  const formatValue = (value: string) => {
    let formattedValue = value;

    if (!formattedValue) return;

    //remove quotes from the start and end
    formattedValue = formattedValue.replace(/^"|"$/g, "");
    //fix a tag
    formattedValue = formattedValue.replace('href=""', 'href="');
    formattedValue = formattedValue.replace('"">', '">');
    //newline
    formattedValue = formattedValue.replace(/<\/li>\n/g, "</li>");
    formattedValue = formattedValue.replace(/\n/g, "<br/>");

    return formattedValue;
  };

  const groupFaqProjectVisible = (groupId: any) => {
    const group = faqsData.find((g) => g.id === groupId);

    if (!group) {
      return [];
    }

    const groupedProjectVisible = group.faqs
      .filter((q) => q.isProjectStageVisible)
      .map((q) => ({
        title: q.question,
        details: q.answer,
      }));

    return groupedProjectVisible;
  };

  return { faqsData, fetchFAQData, groupFaqProjectVisible };
}
